/* eslint-disable max-lines */
/**
 * Strings do Portal do Cliente (E50) — namespace `portal.*` anexado ao ptBR.
 *
 * `max-lines` desligado como no `pt-BR.ts`: o limite existe para módulo com
 * comportamento, e catálogo de strings cresce com a superfície da UI. Quebrá-lo
 * por tamanho espalharia a busca por uma chave em vários arquivos sem separar
 * responsabilidade nenhuma.
 *
 * REGRAS deste namespace:
 *   - `portal.productName` é a ÚNICA fonte da marca "SignCheck" em UI. A marca
 *     só pode aparecer em telas sob components/Portal/, pages/portal/ e neste
 *     arquivo (docs/domain/naming-conventions.md). A área do perito segue com
 *     `ptBR.productName` neutro.
 *   - Vocabulário oficial do PRD (docs/prd/SignCheck_V2_PRD.md §3, decisão 1
 *     validada com o cliente): "Exame primário" — nunca "pré-análise" — e
 *     "laudo certificado"/"certificação" para o serviço acessório.
 *   - Valem as mesmas regras do pt-BR.ts: nenhum termo técnico interno,
 *     nenhuma string de UI hardcoded em componente.
 */
export const ptBRPortal = {
  productName: 'SignCheck',
  brandKicker: 'Portal do cliente',

  nav: {
    sectionCarteira: 'Carteira',
    sectionGestao: 'Gestão',
    visaoGeral: 'Visão geral',
    casos: 'Casos',
    novoCaso: 'Novo caso',
    faturamento: 'Faturamento',
    certificacoes: 'Certificações',
    avisos: 'Avisos',
    usuarios: 'Usuários',
    escritorios: 'Escritórios',
    trilha: 'Trilha de auditoria',
    emBreve: 'Em breve',
    openMenu: 'Abrir menu de navegação',
    closeMenu: 'Fechar menu de navegação',
  },

  shell: {
    userFallback: 'Conta do portal',
  },

  routes: {
    visaoGeral: {
      title: 'Visão geral',
      description:
        'Acompanhe em um só lugar os casos do mês, os exames primários entregues e as certificações requeridas da sua carteira.',
      emptyHint: 'Os indicadores do mês e o movimento recente serão liberados nesta área em breve.',
    },
    faturamento: {
      title: 'Faturamento',
      description: 'Consulte a medição do mês e o extrato de eventos de serviço concluído.',
      emptyHint: 'Nenhum serviço concluído no período selecionado.',
    },
    casos: {
      title: 'Casos',
      description:
        'Acompanhe aqui os casos do seu escritório: cada processo vira um caso, e cada CCB questionada recebe um exame primário próprio, com resultado por contrato.',
      emptyHint: 'A carteira de casos será liberada nesta área em breve.',
    },
    novoCaso: {
      title: 'Novo caso',
      description:
        'O cadastro de casos por processo judicial — declarando uma a uma as CCBs questionadas — será liberado nesta área em breve.',
      emptyHint: 'Enquanto isso, seu contato na equipe de perícia segue recebendo os casos.',
    },
    usuarios: {
      title: 'Usuários',
      description: 'A gestão dos usuários do seu escritório será liberada nesta área em breve.',
      emptyHint: 'Convites e papéis de acesso serão administrados aqui.',
    },
    certificacoes: {
      title: 'Certificações',
      description:
        'Acompanhe as certificações requeridas: em que pé está cada CCB e até quando o laudo é esperado.',
      emptyHint: 'As certificações requeridas aparecerão aqui, com estado e prazo.',
    },
    avisos: {
      title: 'Avisos',
      description: 'O que aconteceu nos seus casos, do mais recente para o mais antigo.',
      emptyHint: 'Os avisos dos seus casos aparecerão aqui.',
    },
  },

  // Carteira de casos do escritório-cliente (E50.F4 — versão mínima; o
  // resultado por CCB e a visão do banco chegam em E50.F6/F7).
  casosLista: {
    eyebrow: 'ACOMPANHAMENTO DE CASOS',
    title: 'Casos',
    subtitle:
      'Cada processo vira um caso; cada CCB questionada recebe um exame primário próprio, com resultado por contrato.',
    novoCaso: 'Novo caso',
    vazioTitulo: 'Nenhum caso na carteira ainda',
    vazioTexto:
      'Abra o primeiro caso do seu escritório: informe o processo, declare as CCBs questionadas e anexe os documentos comparativos.',
    colReferencia: 'Referência',
    colProcesso: 'Processo',
    colRecebido: 'Recebido em',
    colEstado: 'Estado',
    estadoPreparacao: 'Em preparação',
    estadoExame: 'Exame primário em curso',
    estadoConcluido: 'Concluído',
    erroCarregar: 'Não foi possível carregar os casos. Recarregue a página.',
    // E50.F7 — carteira do banco (mockups visao-geral-banco/casos).
    subtitleBanco: 'Carteira de todos os escritórios do banco, com o andamento por caso.',
    vazioBancoTitulo: 'Nenhum caso na carteira ainda',
    vazioBancoTexto:
      'Os casos abertos pelos escritórios do banco aparecerão aqui, com o andamento e as certificações requeridas por CCB.',
    colEscritorio: 'Escritório',
    colCcbs: 'CCBs',
    colCertificacao: 'Certificação',
    colAtualizado: 'Atualizado',
    certUmaRequerida: '1 requerida',
    certVariasRequeridas: '{n} requeridas',
    prazoExcedido: 'Prazo excedido',
  },

  // E50.F7 — Visão geral do BANCO (mockup visao-geral-banco): KPIs reais +
  // movimento recente. "Gastos do mês" é o KPI "em breve" (medição é a
  // próxima Etapa). Sem métrica interna em nenhum número (BR-10).
  visaoGeralBanco: {
    eyebrow: 'PANORAMA DA CARTEIRA',
    subtitle: 'Carteira de todos os escritórios do banco.',
    kpiCasosMes: 'Casos no mês',
    kpiCasosMesFoot: '{n} escritórios ativos',
    kpiExames: 'Exames primários entregues',
    kpiExamesUnidade: 'CCBs',
    kpiExamesFoot: '{n} CCBs em exame',
    kpiCertificacoes: 'Certificações requeridas',
    kpiCertificacoesFoot: '{n} laudos previstos para esta semana',
    kpiGastos: 'Gastos do mês',
    kpiGastosValor: 'Disponível em breve',
    kpiGastosFoot: 'A medição do mês aparecerá aqui.',
    kpiGastosFootDisponivel: 'Soma dos serviços concluídos no mês.',
    movimentoRecente: 'Movimento recente',
    verTodos: 'Ver todos os casos',
    vazioMovimento: 'Os casos abertos pelos escritórios do banco aparecerão aqui.',
    erroCarregar: 'Não foi possível carregar a visão geral. Recarregue a página.',
  },

  // E66.F1 — Visão geral do ESCRITÓRIO: o mesmo formato de KPI do banco, com o
  // movimento do próprio escritório. Só contagens de andamento; o prazo exibido
  // é o da certificação, que já existe no domínio (nada de prazo inventado).
  visaoGeralEscritorio: {
    eyebrow: 'PANORAMA DO ESCRITÓRIO',
    subtitle: 'Andamento dos casos que o escritório enviou para exame.',
    kpiCasosMes: 'Casos no mês',
    kpiCasosMesFootUmBanco: '1 banco atendido',
    kpiCasosMesFootBancos: '{n} bancos atendidos',
    kpiCasosMesFootSemBanco: 'Nenhum banco vinculado',
    kpiExames: 'Exames primários entregues',
    kpiExamesUnidade: 'CCBs',
    kpiExamesUnidadeUma: 'CCB',
    kpiExamesFoot: '{n} CCBs em exame',
    kpiExamesFootUma: '1 CCB em exame',
    kpiExamesFootNenhuma: 'Nenhuma CCB em exame',
    kpiCertificacoes: 'Certificações requeridas',
    kpiCertificacoesFoot: '{n} com prazo nesta semana',
    kpiCertificacoesFootSemPrazo: 'Nenhuma com prazo nesta semana',
    vazio: 'Assim que o primeiro caso for enviado para exame, os números aparecem aqui.',
    erroCarregar: 'Não foi possível carregar a visão geral. Recarregue a página.',
  },

  // E66.F2 — painel das certificações requeridas, para escritório e banco. O
  // estado vem TRADUZIDO por ação (nunca o código cru do servidor) e o prazo é o
  // `sla_due_at` que a certificação já grava: a E66 expõe o prazo que existe e
  // não cria prazo para o exame (ADR-0071 §4).
  painelCertificacoes: {
    eyebrow: 'ACOMPANHAMENTO DAS CERTIFICAÇÕES',
    subtitle: 'Cada CCB com certificação requerida, com o estado atual e a data-limite do laudo.',
    kpiEmAndamento: 'Em andamento',
    kpiEmAndamentoFoot: 'Aguardando o laudo certificado',
    kpiAtrasadas: 'Com prazo vencido',
    kpiAtrasadasFoot: 'Passaram da data-limite',
    kpiVencemHoje: 'Vencem hoje',
    kpiVencemHojeFoot: 'Data-limite é hoje',
    colCaso: 'Caso',
    colCcb: 'CCB',
    colEscritorio: 'Escritório',
    colRequeridaPor: 'Requerida por',
    colEstado: 'Estado',
    colPrazo: 'Prazo',
    estadoRequerida: 'Requerida',
    estadoEmRevisao: 'Em revisão',
    estadoLaudoEntregue: 'Laudo entregue',
    estadoIndefinido: 'Estado não informado',
    marcaAtrasado: 'Prazo vencido',
    marcaVenceHoje: 'Vence hoje',
    prazoEntregue: 'Entregue',
    vazioTitulo: 'Nenhuma certificação requerida ainda',
    vazioTexto:
      'Ao requerer a certificação de uma CCB com exame primário entregue, ela passa a ser acompanhada aqui, com estado e prazo.',
    erroCarregar: 'Não foi possível carregar as certificações. Recarregue a página.',
  },

  // E66.F3 — avisos por evento (ADR-0071). O servidor manda o TIPO do
  // acontecimento e o fato; a frase nasce aqui, na leitura. É o que mantém a
  // régua de neutralidade verificável num lugar só e impede que um texto
  // congelado em banco sobreviva à mudança da regra que o gerou.
  avisos: {
    eyebrow: 'O QUE ACONTECEU NOS SEUS CASOS',
    subtitle: 'Cada aviso vem de um fato registrado no caso — nada é criado só para avisar.',
    contadorNaoVistos: '{n} sem ler',
    contadorTudoLido: 'Tudo lido',
    tipoExameCertificado: 'Exame certificado',
    tipoLaudoHomologado: 'Laudo homologado',
    tipoLaudoGerado: 'Laudo gerado',
    tipoIndefinido: 'Movimentação no caso',
    detalheComCcb: 'Caso {caso} · CCB {ccb}',
    detalheDoCaso: 'Caso {caso}',
    marcarVisto: 'Marcar como lido',
    marcado: 'Lido',
    marcarErro: 'Não foi possível marcar como lido. Tente de novo.',
    abrirCaso: 'Abrir o caso',
    vazioTitulo: 'Nenhum aviso por enquanto',
    vazioTexto:
      'Quando um exame for certificado, um laudo for homologado ou um laudo for gerado nos seus casos, o aviso aparece aqui.',
    erroCarregar: 'Não foi possível carregar os avisos. Recarregue a página.',
  },

  // T4 — medição e extrato do Banco Gestor (E51.F3).
  faturamento: {
    kicker: 'Medição por evento',
    title: 'Faturamento',
    subtitle: 'Confira os serviços concluídos no mês e a faixa aplicada a cada CCB.',
    mesLabel: 'Mês de referência',
    totalMes: 'Total medido no mês',
    totalHint:
      'A soma considera cada evento contra a tabela vigente na data da conclusão do serviço.',
    kpiExames: 'Exames primários no mês',
    kpiExamesFoot: 'Serviços concluídos',
    kpiCertificacoes: 'Certificações requeridas',
    kpiCertificacoesFoot: 'Solicitações registradas no período',
    kpiTicket: 'Ticket médio do exame',
    kpiTicketFoot: 'Por faixa de volume',
    kpiTicketFootVazio: 'Nenhum exame liquidado neste mês',
    kpiAdicional: 'Adicional médio de certificação',
    kpiAdicionalFoot: 'Complemento até o valor agregado',
    kpiAdicionalFootVazio: 'Nenhuma certificação liquidada neste mês',
    faixaNumero: 'Faixa {n}',
    servicoExame: 'Exame primário',
    servicoCertificacao: 'Certificação',
    eventoExame: 'Exame entregue',
    eventoLaudo: 'Laudo assinado',
    extratoTitulo: 'Extrato do mês',
    extratoHint: 'Cada linha nasce de um evento de serviço concluído e mantém a faixa aplicada.',
    extratoVazioTitulo: 'Nenhum serviço concluído neste mês',
    extratoVazioTexto:
      'Quando um exame for entregue ou um laudo for assinado, o evento aparecerá aqui.',
    exportarCsv: 'Exportar CSV',
    exportarEventos: 'Exportar log de eventos',
    exportando: 'Preparando arquivo…',
    erroCarregar: 'Não foi possível carregar a medição. Tente novamente.',
    notaAuditoria:
      'A contagem nasce de eventos imutáveis de serviço concluído. O extrato pode ser reconstituído a partir desses registros e da tabela vigente na data de cada evento.',
    colCaso: 'Caso',
    colCcb: 'CCB',
    colEscritorio: 'Escritório',
    colServico: 'Serviço',
    colFaixa: 'Faixa',
    colVolume: 'Volume no mês',
    colEvento: 'Evento de medição',
    colValor: 'Valor',
    colLiquidado: 'Valor liquidado',
    ccbNaoInformada: 'Não informada',
  },

  // E50.F7 — gestão de usuários do portal (FR-USR, mockup usuarios.html).
  // Rótulos por ambiente (portal-ia §4.6): escritório → Gestor/Analista;
  // banco → Gestor/Usuário comum. Criação direta com senha provisória.
  usuarios: {
    eyebrow: 'PESSOAS COM ACESSO',
    title: 'Usuários',
    subtitle:
      'Gerencie o acesso da sua equipe. Cada usuário opera somente no ambiente do escritório.',
    subtitleBanco: 'Gerencie o acesso da equipe. Cada usuário opera somente no ambiente do banco.',
    adicionar: 'Adicionar usuário',
    colUsuario: 'Usuário',
    colPapel: 'Papel',
    colStatus: 'Status',
    papelGestor: 'Gestor',
    papelAnalista: 'Analista',
    papelUsuarioComum: 'Usuário comum',
    statusAtivo: 'Ativo',
    voce: 'Você',
    tornarGestor: 'Tornar Gestor',
    tornarAnalista: 'Tornar Analista',
    tornarUsuarioComum: 'Tornar Usuário comum',
    confirmarPapelTitulo: 'Alterar papel',
    confirmarPapelTexto: '{usuario} passa a operar como {papel}.',
    desativar: 'Desativar',
    confirmarDesativarTitulo: 'Desativar usuário',
    confirmarDesativarTexto: 'O acesso de {usuario} é encerrado imediatamente.',
    confirmar: 'Confirmar',
    cancelar: 'Cancelar',
    dialogTitulo: 'Adicionar usuário',
    dialogHint:
      'Informe os dados de acesso. A senha é provisória: a pessoa troca no próprio perfil depois do primeiro acesso.',
    dialogNomeLabel: 'Nome (opcional)',
    dialogEmailLabel: 'E-mail',
    dialogSenhaLabel: 'Senha provisória',
    dialogPapelLabel: 'Papel',
    dialogCriar: 'Adicionar',
    dialogCriando: 'Adicionando…',
    dialogEmailInvalido: 'Informe um e-mail válido.',
    dialogSenhaCurta: 'A senha precisa de pelo menos 8 caracteres.',
    erroEmailEmUso: 'Este e-mail já está cadastrado.',
    erroGenerico: 'Não foi possível concluir a ação. Tente novamente.',
    erroCarregar: 'Não foi possível carregar os usuários. Recarregue a página.',
  },

  // E61.F2 — escritórios que o BANCO administra. Só o gestor do banco chega
  // aqui, e só alcança escritório que o próprio banco abriu (ADR-0064 §8).
  escritorios: {
    eyebrow: 'ESTRUTURA DO BANCO',
    title: 'Escritórios',
    subtitle:
      'Abra escritórios vinculados ao banco — o backoffice ou o de um cliente — e administre quem tem acesso a cada um.',
    cadastrar: 'Cadastrar escritório',
    colEscritorio: 'Escritório',
    colCnpj: 'CNPJ',
    colPessoas: 'Pessoas com acesso',
    colAbertoEm: 'Aberto em',
    semCnpj: '—',
    vazioTitulo: 'Nenhum escritório cadastrado ainda',
    vazioTexto:
      'Cadastre o primeiro escritório do banco junto com o gestor que vai operá-lo. Ele passa a abrir casos como qualquer escritório da carteira.',
    erroCarregar: 'Não foi possível carregar os escritórios. Recarregue a página.',
    novoTitulo: 'Cadastrar escritório',
    novoHint:
      'O escritório nasce com o primeiro gestor. A senha é provisória: a pessoa troca no próprio perfil depois do primeiro acesso.',
    novoNomeLabel: 'Nome do escritório',
    novoCnpjLabel: 'CNPJ (opcional)',
    novoGestorNomeLabel: 'Nome do gestor (opcional)',
    novoGestorEmailLabel: 'E-mail do gestor',
    novoGestorSenhaLabel: 'Senha provisória',
    novoCriar: 'Cadastrar',
    novoCriando: 'Cadastrando…',
    novoNomeObrigatorio: 'Informe o nome do escritório.',
    novoCnpjInvalido: 'O CNPJ precisa ter 14 dígitos.',
    voltar: 'Voltar para escritórios',
    naoEncontradoTitulo: 'Escritório não encontrado',
    naoEncontradoTexto: 'Este escritório não está sob administração do banco.',
    pessoasTitulo: 'Pessoas com acesso',
    pessoasSubtitulo: 'Quem opera este escritório e com qual papel.',
    adicionarPessoa: 'Adicionar pessoa',
    // E69.F3 (ADR-0072 §2.8): o banco encerra o escritório que abriu.
    encerrar: 'Encerrar escritório',
    encerrarConfirmacao:
      'Encerrar {nome}? Todas as pessoas dele perdem o acesso agora. Os casos e o histórico ficam guardados, e o escritório sai da sua lista.',
    encerrarConfirmar: 'Confirmar encerramento',
    encerrarCancelar: 'Cancelar',
    encerrarErro: 'Não foi possível encerrar o escritório. Tente novamente.',
    colPessoa: 'Pessoa',
    colPapel: 'Papel',
    colStatus: 'Status',
    papelGestor: 'Gestor',
    papelAnalista: 'Analista',
    papelRevisor: 'Revisor',
    statusAtivo: 'Ativo',
    statusInativo: 'Sem acesso',
    alterarPapel: 'Alterar papel',
    desativar: 'Desativar',
    reativar: 'Reativar',
    confirmarPapelTitulo: 'Alterar papel',
    confirmarPapelHint: 'Escolha o papel de {pessoa} neste escritório.',
    confirmarDesativarTitulo: 'Desativar acesso',
    confirmarDesativarTexto: 'O acesso de {pessoa} é encerrado imediatamente.',
    confirmarReativarTitulo: 'Reativar acesso',
    confirmarReativarTexto: '{pessoa} volta a operar neste escritório.',
    confirmar: 'Confirmar',
    cancelar: 'Cancelar',
    pessoaTitulo: 'Adicionar pessoa',
    pessoaHint: 'A senha é provisória: a pessoa troca no próprio perfil depois do primeiro acesso.',
    pessoaNomeLabel: 'Nome (opcional)',
    pessoaEmailLabel: 'E-mail',
    pessoaSenhaLabel: 'Senha provisória',
    pessoaPapelLabel: 'Papel',
    pessoaCriar: 'Adicionar',
    pessoaCriando: 'Adicionando…',
    emailInvalido: 'Informe um e-mail válido.',
    senhaCurta: 'A senha precisa de pelo menos 8 caracteres.',
    erroEmailEmUso: 'Este e-mail já está cadastrado.',
    erroUltimoGestor: 'O escritório precisa de pelo menos um Gestor ativo.',
    erroGenerico: 'Não foi possível concluir a ação. Tente novamente.',
    erroCarregarPessoas: 'Não foi possível carregar as pessoas. Recarregue a página.',
  },

  // E61.F4 — a trilha do PRÓPRIO banco. A moldura é daqui; o vocabulário dos
  // registros (colunas, ações, estados) vem de `ptBR.trilha`, o mesmo que o
  // console da plataforma usa.
  trilha: {
    eyebrow: 'REGISTRO DE ATIVIDADE',
    title: 'Trilha de auditoria',
    subtitle:
      'Quem fez o quê, quando e sobre quem — no banco e nos escritórios que ele administra.',
    desdeLabel: 'De',
    ateLabel: 'Até',
    autorLabel: 'Quem fez',
    limpar: 'Limpar filtros',
    anterior: 'Anterior',
    proxima: 'Próxima',
    resumo: '{inicio}–{fim} de {total}',
    erroPeriodo: 'Confira as datas do período e tente de novo.',
    erroSemAcesso: 'Você não tem acesso a este registro.',
    erroGenerico: 'Não foi possível carregar o registro. Recarregue a página.',
  },

  // Tutorial de primeiro uso do cadastro (FR-INT-07): questionado × comparativo.
  tutorial: {
    titulo: 'Antes do primeiro caso',
    intro: 'Dois tipos de documento entram no cadastro — e não podem ser misturados:',
    questionadoTitulo: 'CCB questionada',
    questionadoTexto:
      'O contrato com a assinatura contestada. Cada CCB ocupa um slot próprio e gera um exame primário e um laudo próprios dentro do caso.',
    comparativoTitulo: 'Documento comparativo',
    comparativoTexto:
      'Traz assinaturas verdadeiras do autor — RG, procurações, contratos anteriores. Os comparativos são do caso e valem para todas as CCBs.',
    entendi: 'Entendi, começar',
  },

  // Wizard de novo caso (E50.F4 — T1 do PRD, passos Dados → Contratos →
  // Documentos → Revisão).
  wizard: {
    kicker: 'Novo caso',
    stepDados: 'Dados',
    stepContratos: 'Contratos',
    stepDocumentos: 'Documentos',
    stepRevisao: 'Revisão',
    voltar: 'Voltar',
    dados: {
      title: 'Dados do caso',
      subtitle: 'Um processo corresponde a um caso — informe o processo e declare o objeto.',
      tituloLabel: 'Referência do caso',
      tituloHint: 'Como o seu escritório identifica esta pasta.',
      tituloErro: 'Informe a referência do caso.',
      processoLabel: 'Número do processo',
      processoHint: 'Formato CNJ. O processo define o caso.',
      processoErro: 'Informe o número do processo no formato CNJ (0000000-00.0000.0.00.0000).',
      processoConflito:
        'Já existe um caso para este processo. Abra o caso existente na sua carteira.',
      partesLabel: 'Partes (opcional)',
      ccbsLabel: 'Quantas CCBs são objeto da contestação?',
      ccbsHint: 'A quantidade declarada define os slots de contrato do próximo passo.',
      ccbsErro: 'Declare de 1 a 20 CCBs.',
      // E74.F1 (ADR-0075 §1) — modalidade do caso, escolhida aqui e imutável depois.
      modalidadeLabel: 'Modalidade da análise',
      modalidadeHint:
        'Grafotécnica compara o traço da assinatura; eletrônica verifica o protocolo da assinatura eletrônica da CCB.',
      modalidadeGrafotecnica: 'Análise grafotécnica',
      modalidadeEletronica: 'Análise de assinatura eletrônica',
      criarErro: 'Não foi possível criar o caso. Verifique os campos e tente de novo.',
      criando: 'Criando o caso…',
      avancar: 'Avançar para Contratos',
    },
    contratos: {
      title: 'Contratos questionados',
      subtitle:
        'Uma CCB por slot — cada CCB questionada gera um exame primário e um laudo próprios dentro do caso.',
      processoCard: 'Processo (define o caso)',
      ccbsCard: 'CCBs declaradas como objeto',
      alterar: 'Alterar',
      slotTitle: 'CCB {{n}} de {{total}}',
      numeroLabel: 'Nº da CCB',
      numeroPlaceholder: 'Nº a informar',
      numeroDuplicado: 'Este número já está em outro slot.',
      informeNumero: 'Informe o número da CCB para liberar o envio do arquivo.',
      dropTitle: 'Arraste a CCB aqui ou clique para selecionar',
      dropHint: 'Um arquivo por CCB — PDF, DOCX, JPG ou PNG',
      recebido: 'Recebido',
      remover: 'Remover',
      enviando: 'Enviando…',
      slotNote: 'Somente um arquivo de CCB por slot — um segundo arquivo aqui será recusado.',
      umPorSlot: 'Somente um arquivo por slot — envie a outra CCB em um slot próprio.',
      duplicadoNoCaso: 'Este número de CCB já está declarado neste caso.',
      uploadErro: 'Não foi possível receber o arquivo. Tente de novo.',
      tamanhoExcedido: 'Arquivo acima do limite de 50 MB.',
      comoTrataTitulo: 'Como o sistema trata:',
      comoTrataTexto:
        '1 processo = 1 caso. Dentro do caso, cada CCB questionada gera um exame primário e um laudo próprios — datas e assinaturas mudam de contrato para contrato. Os documentos comparativos do próximo passo valem para todas as CCBs.',
      faltamSlots: 'Preencha todos os slots declarados para avançar — ou ajuste a quantidade.',
      avancar: 'Avançar para Documentos',
    },
    documentos: {
      title: 'Documentos comparativos',
      subtitle:
        'Assinaturas verdadeiras do autor para comparação. Os comparativos são do caso e valem para todas as CCBs.',
      dropTitle: 'Arraste os comparativos aqui ou clique para selecionar',
      dropHint: 'RG, procurações, contratos anteriores — PDF, DOCX, JPG ou PNG',
      vazio: 'Nenhum comparativo enviado ainda.',
      remover: 'Remover',
      enviando: 'Enviando…',
      uploadErro: 'Não foi possível receber o arquivo. Tente de novo.',
      tamanhoExcedido: 'Arquivo acima do limite de 50 MB.',
      avancar: 'Avançar para Revisão',
    },
    // E74.F1 — mesmo passo, texto da modalidade eletrônica: não há comparação de
    // traço, então "comparativo" não se aplica; o que entra aqui são as peças do
    // protocolo (certificado de conclusão, comprovantes) que acompanham a CCB.
    documentosEletronica: {
      title: 'Peças do protocolo',
      subtitle:
        'Anexe o certificado de conclusão da plataforma de assinatura, comprovantes e demais peças do protocolo. Comparativos de assinatura não se aplicam à modalidade eletrônica.',
      dropTitle: 'Arraste as peças do protocolo aqui ou clique para selecionar',
      dropHint: 'Certificado de conclusão, comprovantes — PDF, DOCX, JPG ou PNG',
      vazio: 'Nenhuma peça enviada ainda.',
      remover: 'Remover',
      enviando: 'Enviando…',
      uploadErro: 'Não foi possível receber o arquivo. Tente de novo.',
      tamanhoExcedido: 'Arquivo acima do limite de 50 MB.',
      avancar: 'Avançar para Revisão',
    },
    revisao: {
      title: 'Revisão e submissão',
      subtitle:
        'Confira o objeto declarado e confirme as assinaturas encontradas em cada documento antes de submeter.',
      resumoProcesso: 'Processo',
      resumoCcbs: 'CCBs declaradas',
      resumoComparativos: 'Comparativos',
      confirmadas: 'Assinaturas confirmadas',
      pendente: 'Confirmação pendente',
      conferir: 'Conferir assinaturas',
      fecharConferencia: 'Fechar conferência',
      extracaoIntro:
        'Encontramos {{count}} assinatura(s) neste documento. Confirme ou ajuste antes de submeter.',
      extracaoIntroVazia:
        'Nenhuma assinatura foi localizada automaticamente neste documento. Marque a região da assinatura para prosseguir.',
      aguardandoExtracao: 'Preparando o documento…',
      exameInfo:
        'Ao submeter, cada CCB declarada segue para o exame primário. O resultado aparece na sua carteira, por contrato.',
      semComparativoTitulo: 'Submeter sem comparativos?',
      semComparativoTexto:
        'Sem documentos comparativos, o exame primário tende a resultar inconclusivo. Você pode voltar e anexar comparativos, ou submeter assim mesmo.',
      submeterMesmoAssim: 'Submeter assim mesmo',
      cancelar: 'Cancelar',
      submeter: 'Submeter caso',
      submetendo: 'Submetendo o caso…',
      submetido: 'Caso submetido — o exame primário está em andamento.',
      exameFalha:
        'A submissão não foi concluída — o exame primário não foi iniciado para: {{lista}}.',
      exameFalhaParcial:
        'Os demais contratos já seguiram para o exame primário e não serão reenviados.',
      exameFalhaAjuda: 'Seus documentos e confirmações estão guardados. Tente novamente.',
      falhaCcbItem: 'CCB {{numero}}',
      tentarNovamente: 'Tentar novamente',
      // E74.F1 — modalidade eletrônica: sem conferência de assinaturas nem
      // exame automático; a CCB segue para a transcrição do protocolo.
      eletronicaSubtitle: 'Confira as CCBs e as peças do protocolo antes de submeter.',
      eletronicaResumoPecas: 'Peças do protocolo',
      eletronicaRecebido: 'Recebido',
      eletronicaExameInfo:
        'Ao submeter, cada CCB declarada segue para a verificação do protocolo de assinatura eletrônica. O resultado aparece na sua carteira, por contrato.',
      eletronicaSubmetido: 'Caso submetido — a verificação do protocolo será feita por contrato.',
    },
  },

  // T2 — resultado por CCB + requisição de certificação (E50.F6; PRD §5 T2,
  // régua visual docs/design/portal/caso-resultado.html).
  resultado: {
    kicker: 'Caso',
    breadcrumbCasos: 'Casos',
    subTemplate: '{{ccbs}} · aberto em {{data}}',
    umaCcb: '1 CCB neste caso',
    variasCcbs: '{{n}} CCBs neste caso',
    bannerEntregue: 'Exame primário entregue · serviço concluído',
    bannerEmCurso: 'Exame primário em curso',
    pillExaminadas: '{{feitas}} de {{total}} CCBs examinadas',
    cardTitle: 'Resultado por CCB',
    cardHint: 'A certificação é requerida CCB a CCB — a primeira requisição trava a segunda.',
    colCcb: 'CCB',
    colArquivo: 'Arquivo',
    colResultado: 'Resultado do exame primário',
    colCertificacao: 'Certificação',
    requerer: 'Requerer certificação',
    requerendo: 'Requerendo…',
    requeridaPeloEscritorio: 'Requerida pelo escritório',
    requeridaPeloBanco: 'Requerida pelo banco',
    requisicaoSub: '{{data}} · {{autor}} · prazo',
    aguardandoExame: 'Disponível após a entrega do exame.',
    confirmTitle: 'Requerer certificação',
    confirmDescription:
      'A certificação desta CCB será requerida ao corpo pericial, com prazo de 5 dias úteis. A requisição é única por CCB e fica registrada com autor e data.',
    confirmCta: 'Requerer',
    confirmCancel: 'Cancelar',
    conflitoAviso: 'Esta CCB já teve a certificação requerida por {{autor}} ({{ambiente}}).',
    ambienteEscritorio: 'escritório',
    ambienteBanco: 'banco',
    nota: 'O resultado do exame primário é uma triagem técnica, não uma conclusão pericial. A conclusão formal, em razão de verossimilhança, sai apenas no laudo certificado e assinado. A requisição de certificação é única por CCB: feita por um lado, o outro enxerga quem requereu e quando.',
    comparativosTitle: 'Documentos comparativos do caso',
    comparativosHint: 'Valem para todas as CCBs do caso.',
    comparativosVazio: 'Nenhum documento comparativo neste caso.',
    visualizacaoTitle: 'Visualização do exame',
    visualizacaoHint: 'Marcações do exame primário sobre a assinatura questionada.',
    visualizacaoEmExame: 'A visualização fica disponível quando o exame desta CCB for entregue.',
    visualizacaoVazia: 'Sem marcações registradas para esta CCB.',
    // E65.F4 (ADR-0070): o indicativo da triagem aparece SÓ quando o servidor
    // envia a métrica — ambiente escritório; o banco nunca recebe o campo.
    triagemSinteseTitle: 'Síntese da triagem',
    verExame: 'Ver exame',
    ocultarExame: 'Ocultar exame',
    convergencia: 'Convergência',
    divergencia: 'Divergência',
    certificada: 'Laudo certificado',
    certificadaSub: 'Assinado em {{data}} por {{autor}}',
    // E65.F1.T03 — item 11 do E2E do owner: quem assinou vivia só na coluna de
    // certificação, quarta de quatro. Aqui a mesma leitura sobe para o topo do
    // caso, nos dois ambientes.
    assinadoResumo: 'Laudo certificado · assinado em {{data}} por {{autor}}',
    assinadoResumoVarios: '{{n}} laudos certificados · último assinado em {{data}} por {{autor}}',
    baixarLaudo: 'Baixar laudo certificado',
    baixandoLaudo: 'Preparando…',
    erroBaixarLaudo: 'Não foi possível baixar o laudo certificado. Tente novamente.',
    laudoIndisponivel: 'Arquivo indisponível para download.',
    codigoVerificacaoLabel: 'Código de verificação do arquivo',
    // E65.F6 — item 5 do E2E do owner: o rótulo diz QUAL arquivo desce. Numa
    // lista de vários documentos, três controles chamados "baixar" não dizem
    // nada a quem navega por leitor de tela.
    baixarArquivo: 'Baixar {{arquivo}}',
    erroBaixarArquivo: 'Não foi possível baixar. Tente novamente.',
    assinaturaQuestionada: 'assinatura questionada',
    assinaturaComparativa: 'assinatura comparativa',
    erroCarregar: 'Não foi possível carregar o caso. Tente novamente.',
    erroRequerer: 'Não foi possível requerer a certificação. Tente novamente.',
    carregando: 'Carregando o caso…',

    // E80.F3 — um lugar só onde o cliente pega o que é dele. O relatório da
    // análise preliminar nasce aqui (na eletrônica ele não existia em documento
    // nenhum); o laudo continua vindo do bloco da certificação, que já sabe
    // quando ele existe.
    saidas: {
      titulo: 'Saídas digitais do caso',
      hint: 'O que este caso já produziu, pronto para baixar.',
      relatorioTitulo: 'Relatório da análise preliminar',
      relatorioSub:
        'Registro formatado da análise preliminar deste caso. Não é laudo pericial e não possui responsável técnico.',
      baixarRelatorio: 'Baixar relatório da análise preliminar',
      baixandoRelatorio: 'Preparando…',
      // Indisponível diz POR QUÊ. Botão morto obriga o cliente a adivinhar se
      // o documento não existe ou se o sistema falhou.
      relatorioIndisponivel: 'Disponível quando o exame de todas as peças deste caso for entregue.',
      erroBaixarRelatorio: 'Não foi possível baixar o relatório. Tente novamente.',
      laudoTitulo: 'Laudo certificado',
      laudoPendente: 'Disponível quando o laudo certificado for entregue.',
    },
  },
} as const;
